import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react';
import produce from 'immer';
import { useLocation } from 'react-router-dom';

import { unimplemented } from '@utils/commonUtils';
import { WhatsNew } from '@components/WhatsNew';
import { UserApplication } from '@auth_module/services';

type AccountContextKeys = 'account.user' | 'account.is_whats_new_visible';

type Action = {
  type: AccountContextKeys;
  payload: boolean;
};

const ACCOUNT_CONTEXT_KEYS = {
  IS_WHATS_NEW_VISIBLE: 'account.is_whats_new_visible' as AccountContextKeys,
};

export interface IAccountContext {
  currentApplication: UserApplication | null;
  currentPage: string;
  isWhatsNewVisible: boolean;
  setIsWhatsNewVisible: (isVisible: boolean) => void;
}

const initialState = {
  currentApplication: null,
  currentPage: '/',
  isWhatsNewVisible: false,
  setIsWhatsNewVisible: unimplemented,
};

const reducer = produce((draft: IAccountContext, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case ACCOUNT_CONTEXT_KEYS.IS_WHATS_NEW_VISIBLE:
      draft.isWhatsNewVisible = payload;
      break;
    default:
      break;
  }
}, initialState);

export const AccountContext = createContext<IAccountContext>(initialState);

export const AccountProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { pathname } = useLocation();

  const currentApplication: UserApplication | null = useMemo(() => {
    const _pathname = pathname.split('/');

    if (_pathname.includes('self-service-api')) {
      return 'api';
    }

    if (_pathname.includes('dashboard')) {
      return 'promohub';
    }

    // for now /account/settings we are using for organizations and we see it as part of audio-insights application
    if (
      _pathname.includes('insights') ||
      (_pathname.includes('account') && _pathname.includes('settings'))
    ) {
      return 'audio-insights';
    }

    return null;
  }, [location.pathname]);

  const currentPage = useMemo(() => {
    const _pathname = pathname.split('/');

    if (_pathname.includes('account') && _pathname.includes('settings')) {
      return 'organizations';
    }

    if (_pathname.includes('insights') && _pathname.includes('integrations')) {
      return 'integrations';
    }

    if (_pathname.includes('insights') && _pathname.includes('shows')) {
      return 'shows';
    }

    if (_pathname.includes('insights') && _pathname.includes('episodes')) {
      return 'episodes';
    }

    if (_pathname.includes('insights')) {
      return 'overview';
    }

    return '/';
  }, [location.pathname]);

  const setIsWhatsNewVisible = (isVisible: boolean) => {
    dispatch({ type: ACCOUNT_CONTEXT_KEYS.IS_WHATS_NEW_VISIBLE, payload: isVisible });
  };

  const value = {
    ...state,
    currentApplication,
    currentPage,
    setIsWhatsNewVisible,
  };

  return (
    <AccountContext.Provider value={value}>
      <WhatsNew isVisible={state.isWhatsNewVisible} setIsVisible={setIsWhatsNewVisible} />
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountContext = () => useContext(AccountContext);
